// client/src/contexts/TransactionContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  getTransactions, 
  createTransaction, 
  updateTransaction, 
  deleteTransaction 
} from '../services/transactionService';
import { useAuth } from './AuthContext';
import useAuthReset from '../hooks/useAuthReset';

const TransactionContext = createContext();

export const useTransaction = () => useContext(TransactionContext);

export const TransactionProvider = ({ children }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const { isAuthenticated, user } = useAuth();
  const userId = user?._id ?? user?.id ?? null;

  // Privacy guard: drop the cached transaction list as soon as the
  // authenticated user changes. Prevents User A's transactions from
  // being briefly visible to User B on a shared device.
  const resetTransactionState = useCallback(() => {
    setTransactions([]);
    setError(null);
  }, []);
  useAuthReset(resetTransactionState);

  // Load transactions when component mounts if user is authenticated
  useEffect(() => {
    if (isAuthenticated && userId) {
      fetchTransactions();
    } else {
      // Logged out → drop cached list immediately.
      setTransactions([]);
      setError(null);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userId]);
  
  
  // Fetch all transactions from the server
  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const data = await getTransactions();
      setTransactions(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to load transactions. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Add a new transaction
  const addTransaction = async (transactionData) => {
    setLoading(true);
    try {
      const newTransaction = await createTransaction(transactionData);
      setTransactions([newTransaction, ...transactions]);
      setError(null);
      return newTransaction;
    } catch (err) {
      console.error('Error adding transaction:', err);
      setError('Failed to add transaction. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Update an existing transaction
  const editTransaction = async (id, transactionData) => {
    setLoading(true);
    try {
      const updatedTransaction = await updateTransaction(id, transactionData);
      setTransactions(
        transactions.map(transaction => 
          transaction._id === id ? updatedTransaction : transaction
        )
      );
      setError(null);
      return updatedTransaction;
    } catch (err) {
      console.error('Error updating transaction:', err);
      setError('Failed to update transaction. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Remove a transaction
  const removeTransaction = async (id) => {
    setLoading(true);
    try {
      await deleteTransaction(id);
      setTransactions(transactions.filter(transaction => transaction._id !== id));
      setError(null);
    } catch (err) {
      console.error('Error deleting transaction:', err);
      setError('Failed to delete transaction. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Get transactions for a specific category
  const getTransactionsByCategoryId = (categoryId) => {
    return transactions.filter(transaction => transaction.categoryId === categoryId);
  };
  
  // Calculate total spent for a specific category
  const calculateCategorySpent = (categoryId) => {
    return getTransactionsByCategoryId(categoryId)
      .filter(t => t.type === 'expense')
      .reduce((total, transaction) => total + transaction.amount, 0);
  };
  
  const value = {
    transactions,
    loading,
    error,
    fetchTransactions,
    addTransaction,
    editTransaction,
    removeTransaction,
    getTransactionsByCategoryId,
    calculateCategorySpent
  };
  
  return (
    <TransactionContext.Provider value={value}>
      {children}
    </TransactionContext.Provider>
  );
};